import { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useCourseOptions } from "@/lib/useCourseOptions";

export default function Contact({ data, branding }) {
  const phones = normalisePhones(data, branding);
  return (
    <section id="contact" data-testid="section-contact" className="section-pad bg-[#FDFBF7]">
      <div className="container-pad">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <div className="kicker">{data.kicker || "Get In Touch"}</div>
            <h2 className="heading-main mt-4">{data.title}</h2>
            <p className="heading-sub">{data.subtitle}</p>

            <div className="mt-9 space-y-3" data-testid="contact-phones">
              {phones.map((p, i) => (
                <PhoneRow key={i} label={p.label} number={p.number} index={i} />
              ))}
              {(() => {
                const email = (data.email || "").trim();
                const address = (data.address || "").trim();
                if (!email && !address) return null;
                return (
                  <div className="pt-2 space-y-3">
                    {email && (
                      <ContactRow
                        icon={Mail}
                        label="Email"
                        value={email}
                        href={`mailto:${email}`}
                        testid="contact-email"
                      />
                    )}
                    {address && (
                      <ContactRow
                        icon={MapPin}
                        label="Visit"
                        value={address}
                        href={data.mapLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                        external
                        testid="contact-address"
                      />
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="mt-9 rounded-2xl bg-[#1E3A8A] text-white p-6 shadow-lg">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-teal-300">Counselling Hours</div>
              <div className="mt-2 text-lg font-bold">Mon – Sat · 10:00 AM – 7:00 PM</div>
              <p className="mt-2 text-sm text-slate-200/85">Speak directly to an admission counsellor. We'll guide you through course options, fees and college admissions.</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ContactLeadForm ctaLabel={data.ctaLabel || "Send Enquiry"} />
          </div>
        </div>
      </div>
    </section>
  );
}

function normalisePhones(data, branding) {
  const raw = Array.isArray(data?.phones) && data.phones.length ? data.phones : null;
  if (raw) return raw; // show every configured slot (even empty ones)
  const single = data?.phone || branding?.primaryPhone;
  return single ? [{ label: "Call us", number: single }] : [];
}

function PhoneRow({ label, number, index }) {
  const hasNumber = !!(number && number.trim());
  const inner = (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#1E3A8A]/8 text-[#1E3A8A] grid place-items-center shrink-0">
        <Phone className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">{label || "Call us"}</div>
        <div className={`text-base font-semibold mt-0.5 ${hasNumber ? "text-[#0F172A]" : "text-slate-400 italic"}`}>
          {hasNumber ? number : "Add number in CMS →"}
        </div>
      </div>
    </div>
  );
  return hasNumber ? (
    <a
      href={`tel:${number}`}
      className="block hover:translate-x-1 transition"
      data-testid={`contact-phone-${index}`}
    >
      {inner}
    </a>
  ) : (
    <div data-testid={`contact-phone-${index}`}>{inner}</div>
  );
}

function ContactLeadForm({ ctaLabel }) {
  const options = useCourseOptions();
  const [form, setForm] = useState({ name: "", mobile: "", course: options[0] || "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Please enter your name and mobile number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source: "contact" });
      setDone(true);
      toast.success("Enquiry submitted! We'll be in touch soon.");
      setForm({ name: "", mobile: "", course: options[0] || "", message: "" });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative" data-testid="contact-lead-form-card">
      <div className="absolute -top-3 -left-3 w-24 h-24 rounded-full bg-teal-100 -z-10 hidden md:block" />
      <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-full bg-[#1E3A8A]/10 -z-10 hidden md:block" />

      <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_40px_-12px_rgba(15,23,42,0.15)] overflow-hidden">
        <div className="bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white px-7 md:px-9 py-7">
          <div className="text-xs tracking-[0.18em] uppercase font-semibold text-teal-300">Quick Enquiry</div>
          <h3 className="mt-2 text-2xl md:text-3xl font-bold leading-snug">Start your Pharma career today</h3>
          <p className="mt-2 text-sm text-slate-200/85 max-w-md">Fill the form below and our admissions team will reach out within one business day.</p>
        </div>

        {done ? (
          <div className="p-10 text-center" data-testid="contact-form-success">
            <div className="w-16 h-16 rounded-full bg-teal-100 grid place-items-center mx-auto text-teal-700">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h4 className="mt-5 font-bold text-xl text-[#0F172A]">Thank you!</h4>
            <p className="text-sm text-slate-600 mt-1 max-w-sm mx-auto">Your enquiry has been received. Our counsellor will get in touch with you shortly.</p>
            <button onClick={() => setDone(false)} className="btn-ghost mt-7" data-testid="contact-form-new">
              Submit another enquiry
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 md:p-9 grid gap-5" data-testid="contact-lead-form">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Your full name" testid="contact-name-input" required />
              <Field label="Mobile Number" type="tel" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v.replace(/[^\d+ -]/g, "") })} placeholder="+91 9XXXXXXXXX" testid="contact-mobile-input" required />
            </div>
            <SelectField
              label="Interested Course"
              value={form.course}
              onChange={(v) => setForm({ ...form, course: v })}
              options={options}
              testid="contact-course-select"
            />
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Your Message / Query</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Which course are you interested in? Any questions?"
                rows={5}
                data-testid="contact-message-input"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15 resize-none"
              />
            </div>
            <button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="btn-accent w-full md:w-auto md:self-start text-base disabled:opacity-60">
              {submitting ? "Submitting..." : <>{ctaLabel} <Send className="w-4 h-4" /></>}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>
            <p className="text-[11px] text-slate-400">By submitting, you agree to be contacted by Sunbright Pharma Academy regarding your enquiry.</p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15"
      />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm bg-white focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, external, testid }) {
  const inner = (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#1E3A8A]/8 text-[#1E3A8A] grid place-items-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">{label}</div>
        <div className="text-base font-semibold text-[#0F172A] mt-0.5">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a
      href={href}
      data-testid={testid}
      className="block hover:translate-x-1 transition"
      {...(external ? { target: "_blank", rel: "noreferrer noopener" } : {})}
    >
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}
